import React, { useState, useEffect } from 'react';
import { ShieldOff, Plus, Download, Upload, Search, Edit2, Trash2, X, AlertCircle, CheckCircle, Calendar, Clock } from 'lucide-react';
import { dndApi } from '../../services/ivrApi';
import OrganizationFilter from '../../components/Filters/OrganizationFilter';
import { useScopedOrgId } from '../../hooks/useScopedOrgId';
import type { DNDEntry } from '../../types/ivr';

// ============================================================================
// DND (Do Not Disturb) Management Page
// ============================================================================

const DNDManagement: React.FC = () => {
  const [dndEntries, setDndEntries] = useState<DNDEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDND, setEditingDND] = useState<DNDEntry | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  // Business admins get their own organization implicitly; provider-level roles
  // choose one. See useScopedOrgId — before it, every role outside the chooser
  // list was left with no organization at all and a dead page.
  const { selectedOrgId, setSelectedOrgId, showOrgFilter } = useScopedOrgId();

  useEffect(() => {
    if (selectedOrgId) {
      loadDNDEntries();
    } else {
      setDndEntries([]);
    }
  }, [selectedOrgId, currentPage]);

  const loadDNDEntries = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    try {
      // Use search endpoint for pagination support
      const response = await dndApi.search(selectedOrgId, {
        page: currentPage - 1, // Backend uses 0-indexed pages
        size: itemsPerPage,
      });

      if (response.success && response.data) {
        setDndEntries(response.data.content || []);
        setTotalPages(response.data.totalPages || 1);
      } else {
        // Fallback to getAll if search fails
        const allResponse = await dndApi.getAll(selectedOrgId);
        if (allResponse.success && allResponse.data) {
          setDndEntries(allResponse.data);
          setTotalPages(1);
        } else {
          showNotification('error', 'Failed to load blocked numbers');
        }
      }
    } catch (error) {
      showNotification('error', 'Network error loading blocked numbers');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDND = async (dnd: Partial<DNDEntry>) => {
    try {
      // Transform frontend DNDEntry fields to backend expected fields
      const dndData = {
        phoneNumber: dnd.msisdn || '',
        channel: 'VOICE' as const, // Default to VOICE channel for phone blocking
        reason: dnd.block_reason || undefined,
        source: 'MANUAL',
        expiresAt: dnd.blocked_until || undefined,
        enabled: dnd.is_active !== false,
      };

      const response = editingDND
        ? await dndApi.update(selectedOrgId, editingDND.id!, dndData)
        : await dndApi.create(selectedOrgId, dndData);

      if (response.success) {
        await loadDNDEntries();
        setShowModal(false);
        setEditingDND(null);
        showNotification(
          'success',
          editingDND ? 'Blocked number updated successfully' : 'Number blocked successfully'
        );
      } else {
        showNotification('error', response.error || 'Failed to save blocked number');
      }
    } catch (error) {
      showNotification('error', 'Network error saving blocked number');
    }
  };

  const handleDeleteDND = async (id: number, msisdn: string) => {
    if (confirm(`Remove ${msisdn} from blocked list?`)) {
      try {
        const response = await dndApi.delete(selectedOrgId, id);
        if (response.success) {
          await loadDNDEntries();
          showNotification('success', 'Blocked number removed successfully');
        } else {
          showNotification('error', response.error || 'Failed to remove blocked number');
        }
      } catch (error) {
        showNotification('error', 'Network error removing blocked number');
      }
    }
  };

  const handleCleanupExpired = async () => {
    if (!selectedOrgId) return;
    // Note: Cleanup expired is a client-side filter now since backend doesn't have this endpoint
    // Simply reload and let expired entries be filtered out
    try {
      await loadDNDEntries();
      showNotification('success', 'Refreshed entries - expired entries filtered');
    } catch (error) {
      showNotification('error', 'Network error during refresh');
    }
  };

  const handleBulkImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedOrgId) return;

    setLoading(true);
    try {
      const response = await dndApi.bulkImport(selectedOrgId, file);
      if (response.success) {
        await loadDNDEntries();
        showNotification('success', `Blocked ${response.data?.imported || 0} numbers`);
      } else {
        showNotification('error', response.error || 'Bulk import failed');
      }
    } catch (error) {
      showNotification('error', 'Network error during bulk import');
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleExport = async () => {
    if (!selectedOrgId) return;

    try {
      const blob = await dndApi.export(selectedOrgId);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dnd_blocklist_${selectedOrgId}_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        showNotification('success', 'Blocklist exported successfully');
      }
    } catch (error) {
      showNotification('error', 'Failed to export blocklist');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const filteredDNDs = dndEntries.filter(
    dnd =>
      dnd.msisdn.includes(searchTerm) ||
      dnd.block_reason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dnd.custom_message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isExpired = (entry: DNDEntry): boolean => {
    if (entry.block_type === 'temporary' && entry.blocked_until) {
      return new Date(entry.blocked_until) < new Date();
    }
    return false;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <ShieldOff className="w-6 h-6 text-red-500" />
            <span>DND Blocked Numbers</span>
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage blocked callers with hard/soft blocking and scheduled restrictions
          </p>
        </div>
        <button
          onClick={() => {
            setEditingDND(null);
            setShowModal(true);
          }}
          disabled={!selectedOrgId}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Plus className="w-4 h-4" />
          <span>Block Number</span>
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`flex items-center space-x-2 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-400'
              : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Filter - Only for admin roles */}
      {showOrgFilter && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <OrganizationFilter
            value={selectedOrgId}
            onChange={(value) => {
              setSelectedOrgId(value);
              setCurrentPage(1); // Reset pagination when business changes
            }}
            className="w-full md:w-96"
            requireSelection={true}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show message if no org selected */}
      {showOrgFilter && !selectedOrgId && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <p className="text-blue-800 dark:text-blue-400 text-center">
            Please select a business from the dropdown above to view blocked numbers.
          </p>
        </div>
      )}

      {selectedOrgId && (
        <>
          {/* Actions Bar */}
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by phone number, reason, or message..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleCleanupExpired}
                className="flex items-center space-x-2 px-4 py-2 border border-orange-300 dark:border-orange-600 text-orange-700 dark:text-orange-400 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition"
              >
                <Clock className="w-4 h-4" />
                <span>Cleanup Expired</span>
              </button>
              <label className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition text-gray-700 dark:text-gray-300">
                <Upload className="w-4 h-4" />
                <span>Import CSV</span>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleBulkImport}
                  className="hidden"
                />
              </label>
              <button
                onClick={handleExport}
                disabled={dndEntries.length === 0}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-gray-700 dark:text-gray-300"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
            </div>
          </div>

          {/* DND List Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Loading blocked numbers...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Phone Number
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Block Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Expires / Schedule
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredDNDs.map(dnd => (
                      <tr key={dnd.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition ${isExpired(dnd) ? 'opacity-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{dnd.msisdn}</div>
                          {dnd.block_reason && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {dnd.block_reason}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              dnd.block_type === 'permanent'
                                ? 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-400'
                                : dnd.block_type === 'temporary'
                                ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-400'
                                : 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-400'
                            }`}
                          >
                            {dnd.block_type === 'permanent' ? 'Permanent' : dnd.block_type === 'temporary' ? 'Temporary' : 'Scheduled'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              dnd.severity === 'hard'
                                ? 'bg-gray-900 dark:bg-gray-600 text-white'
                                : 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-400'
                            }`}
                          >
                            {dnd.severity === 'hard' ? 'Hard Block' : 'Soft Block'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {dnd.block_type === 'temporary' && dnd.blocked_until ? (
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(dnd.blocked_until).toLocaleDateString()}</span>
                            </div>
                          ) : dnd.block_type === 'scheduled' && dnd.schedule_days ? (
                            <div className="flex items-center space-x-1">
                              <Clock className="w-3 h-3" />
                              <span>{dnd.schedule_days}</span>
                            </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              isExpired(dnd)
                                ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                : dnd.is_active
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-400'
                            }`}
                          >
                            {isExpired(dnd) ? 'Expired' : dnd.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => {
                              setEditingDND(dnd);
                              setShowModal(true);
                            }}
                            className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mr-3 transition"
                            title="Edit blocked number"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDND(dnd.id!, dnd.msisdn)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition"
                            title="Remove from blocklist"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredDNDs.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <ShieldOff className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm">
                      {searchTerm ? 'No blocked numbers found matching your search' : 'No blocked numbers yet'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-600 transition text-gray-700 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <DNDModal
          dnd={editingDND}
          businessId={selectedOrgId}
          onSave={handleSaveDND}
          onClose={() => {
            setShowModal(false);
            setEditingDND(null);
          }}
        />
      )}
    </div>
  );
};

// ============================================================================
// DND Modal Component
// ============================================================================

interface DNDModalProps {
  dnd: DNDEntry | null;
  businessId: string;
  onSave: (dnd: Partial<DNDEntry>) => void;
  onClose: () => void;
}

const DNDModal: React.FC<DNDModalProps> = ({ dnd, businessId, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<DNDEntry>>(
    dnd || {
      msisdn: '',
      block_type: 'permanent',
      severity: 'hard',
      is_active: true,
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate phone number (E.164 format)
    if (!formData.msisdn) {
      newErrors.msisdn = 'Phone number is required';
    } else if (!/^\+?[1-9]\d{10,14}$/.test(formData.msisdn)) {
      newErrors.msisdn = 'Invalid phone number format (E.164)';
    }

    // Validate blocked_until for temporary blocks
    if (formData.block_type === 'temporary' && !formData.blocked_until) {
      newErrors.blocked_until = 'Expiry date is required for temporary blocks';
    }

    // Validate schedule for scheduled blocks
    if (formData.block_type === 'scheduled') {
      if (!formData.schedule_days) {
        newErrors.schedule_days = 'Days are required for scheduled blocks';
      }
      if (!formData.schedule_start_time) {
        newErrors.schedule_start_time = 'Start time is required';
      }
      if (!formData.schedule_end_time) {
        newErrors.schedule_end_time = 'End time is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <ShieldOff className="w-5 h-5 text-red-500" />
            <span>{dnd ? 'Edit Blocked Number' : 'Block Phone Number'}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone Number (E.164) <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              placeholder="+1234567890"
              value={formData.msisdn}
              onChange={e => setFormData({ ...formData, msisdn: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                errors.msisdn ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.msisdn && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.msisdn}</p>
            )}
          </div>

          {/* Block Type and Severity */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Block Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.block_type}
                onChange={e =>
                  setFormData({ ...formData, block_type: e.target.value as DNDEntry['block_type'] })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="permanent">Permanent</option>
                <option value="temporary">Temporary</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Block Severity <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.severity}
                onChange={e =>
                  setFormData({ ...formData, severity: e.target.value as 'hard' | 'soft' })
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="hard">Hard (No message, immediate hangup)</option>
                <option value="soft">Soft (Play message then hangup)</option>
              </select>
            </div>
          </div>

          {/* Temporary Block Expiry */}
          {formData.block_type === 'temporary' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Block Until <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={
                  formData.blocked_until
                    ? new Date(formData.blocked_until).toISOString().slice(0, 16)
                    : ''
                }
                onChange={e =>
                  setFormData({ ...formData, blocked_until: e.target.value ? new Date(e.target.value).toISOString() : undefined })
                }
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white ${
                  errors.blocked_until ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.blocked_until && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.blocked_until}</p>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Block will automatically expire after this date
              </p>
            </div>
          )}

          {/* Scheduled Block Configuration */}
          {formData.block_type === 'scheduled' && (
            <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Days of Week <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Mon,Wed,Fri or *"
                  value={formData.schedule_days || ''}
                  onChange={e => setFormData({ ...formData, schedule_days: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${
                    errors.schedule_days ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                  }`}
                />
                {errors.schedule_days && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.schedule_days}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Use * for all days, or comma-separated: Mon,Tue,Wed,Thu,Fri,Sat,Sun
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={formData.schedule_start_time || ''}
                    onChange={e => setFormData({ ...formData, schedule_start_time: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white ${
                      errors.schedule_start_time ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.schedule_start_time && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.schedule_start_time}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={formData.schedule_end_time || ''}
                    onChange={e => setFormData({ ...formData, schedule_end_time: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white ${
                      errors.schedule_end_time ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                    }`}
                  />
                  {errors.schedule_end_time && (
                    <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.schedule_end_time}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Custom Message (Soft Block) */}
          {formData.severity === 'soft' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Custom Block Message
              </label>
              <textarea
                placeholder="This number is currently blocked..."
                value={formData.custom_message || ''}
                onChange={e => setFormData({ ...formData, custom_message: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Message played before hanging up (soft block only)
              </p>
            </div>
          )}

          {/* Block Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Block Reason
            </label>
            <input
              type="text"
              placeholder="e.g., Spam, Harassment, Customer Request"
              value={formData.block_reason || ''}
              onChange={e => setFormData({ ...formData, block_reason: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
            />
          </div>

          {/* Active Checkbox */}
          <div className="pt-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active !== false}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="mt-1 w-4 h-4 text-red-600 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500 dark:bg-gray-700"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Active</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Block is currently in effect
                </div>
              </div>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center space-x-2"
            >
              <ShieldOff className="w-4 h-4" />
              <span>{dnd ? 'Update Block' : 'Block Number'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DNDManagement;
